import axios from "axios";

const BASE_URL = process.env.REACT_APP_SOCKET_URL;
let xApiKey = process.env.REACT_APP_API_KEY;

// ✅ Memory mein access token (global variable)
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
  accessToken = null;
};

// =========================================
// Helper: logout + redirect
// =========================================
const forceLogout = () => {
  clearAccessToken();
  localStorage.removeItem("user");
  localStorage.removeItem("selectedStore");
  localStorage.removeItem("selectedStoreID");
  localStorage.removeItem("selectedStoreName");
  // window.location.href = "/";
};

// =========================================
// Main axios instance
// =========================================
const axiosInstance = axios.create({
  baseURL: BASE_URL + "api/admins/",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// =========================================
// ✅ Separate instance SIRF refresh ke liye
// Ye response interceptor se nahi guzrega
// Isliye infinite loop ya transform issue nahi hoga
// =========================================
const refreshInstance = axios.create({
  baseURL: BASE_URL + "api/admins/",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// =========================================
// REQUEST INTERCEPTOR
// =========================================
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const selectedStore = localStorage.getItem("selectedStore");
    if (selectedStore) {
      config.headers["x-store-context"] = selectedStore;
    }

    config.headers["x-api-key"] = xApiKey;
    return config;
  },
  (error) => Promise.reject(error),
);

// =========================================
// RESPONSE INTERCEPTOR
// =========================================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data instanceof Blob) {
      return response.data;
    }

    const { code, message, data } = response.data || {};
    return {
      code: code || 500,
      message: message || "Something went wrong! Try Again",
      data: data || null,
    };
  },

  // ✅ Error handler
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorData = error.response?.data;

    // Invalid store context — clear it
    if (errorData?.message === "Invalid store context token") {
      localStorage.removeItem("selectedStore");
    }

    // 401 handle karo
    if (status === 401) {
      // Agar ye request already retry ho chuki hai → logout
      if (originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
      }

      // Agar refresh already chal raha hai → queue mein daal do
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // ✅ Pehli baar 401 aayi — refresh try karo
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // ✅ refreshInstance use karo — ye axiosInstance ke interceptor se nahi guzrega
        // Isliye response { code, message, data } mein transform nahi hoga
        // Seedha axios raw response milega: res.data.data.accessToken ya res.data.accessToken
        const res = await refreshInstance.post(
          "auth/refresh-token",
          {},
          { withCredentials: true },
        );

        // ✅ Apne API ke actual response structure ke hisaab se adjust karo
        // Common cases:
        // Case 1: { data: { accessToken: "..." } }
        // Case 2: { accessToken: "..." }
        // Case 3: { data: { data: { accessToken: "..." } } }

        const newAccessToken =
          res?.data?.data?.accessToken || // nested data.data
          res?.data?.accessToken || // direct data
          null;

        if (!newAccessToken) {
          console.error("Refresh response structure:", res?.data);
          throw new Error("accessToken not found in refresh response");
        }

        setAccessToken(newAccessToken);
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error(
          "❌ Token refresh failed:",
          refreshError?.response?.data || refreshError.message,
        );
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
